import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Button } from "Components/Common/Button/Button";
import { Toggle } from "Components/Common/ToggleBtn/Toggle";
import {
  QUESTION_TYPES,
  CONDITION_OPS,
  WIDGET_TYPES,
  parseConditionValueForOp,
  conditionValueToText,
} from "./constants";

const selectClass =
  "mt-1 block w-full pl-2 pr-8 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none sm:text-sm";
const inputClass =
  "mt-1 block w-full px-2 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none sm:text-sm";
const labelClass = "block text-sm font-semibold text-gray-700";

const Field = ({ label, hint, children }) => (
  <div>
    <label className={labelClass}>{label}</label>
    {children}
    {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
  </div>
);

const slugifyKey = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const emptyDisqualifier = () => ({ op: "eq", value: "true", action: "WARN", message: "" });

export const QuestionModal = ({
  open,
  setOpen,
  question,
  earlierQuestions,
  existingKeys,
  onSave,
}) => {
  const [key, setKey] = useState(question?.key || "");
  const [title, setTitle] = useState(question?.title || "");
  const [subtitle, setSubtitle] = useState(question?.subtitle || "");
  const [type, setType] = useState(question?.type || "BOOLEAN");
  const [required, setRequired] = useState(question ? question.required !== false : true);
  const [options, setOptions] = useState(
    (question?.options || []).map((option) => ({
      value: option.value,
      label: option.label,
      disqualifyAction: option.disqualify?.action || "",
      disqualifyMessage: option.disqualify?.message || "",
    }))
  );
  const [validation, setValidation] = useState(question?.validation || {});
  const [showIf, setShowIf] = useState(
    (question?.showIf || []).map((condition) => ({
      questionKey: condition.questionKey,
      op: condition.op,
      value: conditionValueToText(condition.value),
    }))
  );
  const [disqualify, setDisqualify] = useState(
    question?.disqualify
      ? {
          op: question.disqualify.op,
          value: conditionValueToText(question.disqualify.value),
          action: question.disqualify.action,
          message: question.disqualify.message || "",
        }
      : null
  );
  const [widget, setWidget] = useState(question?.widget || "REFILL_MED_PICKER");
  const [widgetConfig, setWidgetConfig] = useState(
    question?.widgetConfig || { includedMeds: 4, allow90Day: true }
  );
  const [formError, setFormError] = useState("");

  const isSelect = type === "SINGLE_SELECT" || type === "MULTI_SELECT";

  const setValidationField = (field, raw) => {
    const value = raw === "" ? undefined : Number(raw);
    setValidation((current) => ({ ...current, [field]: value }));
  };

  const buildQuestion = () => {
    const built = {
      key,
      type,
      title,
      subtitle: subtitle || undefined,
      required,
    };
    if (isSelect) {
      built.options = options.map((option) => ({
        value: option.value,
        label: option.label,
        disqualify: option.disqualifyAction
          ? {
              op: "eq",
              value: option.value,
              action: option.disqualifyAction,
              message: option.disqualifyMessage || undefined,
            }
          : undefined,
      }));
    }
    if (type === "DATE") {
      built.validation = {
        minDate: validation.minDate || undefined,
        maxDate: validation.maxDate || undefined,
      };
    } else if (Object.keys(validation).length) {
      built.validation = validation;
    }
    if (showIf.length) {
      built.showIf = showIf.map((condition) => ({
        questionKey: condition.questionKey,
        op: condition.op,
        value:
          condition.op === "answered"
            ? undefined
            : parseConditionValueForOp(condition.op, condition.value),
      }));
    }
    if (disqualify) {
      built.disqualify = {
        op: disqualify.op,
        value: parseConditionValueForOp(disqualify.op, disqualify.value),
        action: disqualify.action,
        message: disqualify.message || undefined,
      };
    }
    if (type === "WIDGET") {
      built.widget = widget;
      built.widgetConfig = widgetConfig;
    }
    return built;
  };

  const save = () => {
    if (!key || !/^[a-z0-9_]+$/.test(key)) {
      setFormError("Key must be snake_case (lowercase letters, numbers, underscores)");
      return;
    }
    if (existingKeys.includes(key)) {
      setFormError(`Key "${key}" is already used by another question`);
      return;
    }
    if (!title) {
      setFormError("Title is required");
      return;
    }
    if (isSelect && options.length === 0) {
      setFormError("Choice questions need at least one option");
      return;
    }
    if (isSelect && options.some((option) => !option.value || !option.label)) {
      setFormError("Every option needs a value and a label");
      return;
    }
    if (showIf.some((condition) => !condition.questionKey)) {
      setFormError("Every show-if rule needs a question selected");
      return;
    }
    onSave(buildQuestion());
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" onClose={() => setOpen(false)}>
        <div className="flex items-center justify-center min-h-screen p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div
              className="relative bg-white rounded-lg shadow-xl text-left w-full max-w-2xl p-6 overflow-y-auto"
              style={{ maxHeight: "85vh" }}
            >
              <Dialog.Title as="h3" className="text-lg font-medium text-gray-900 mb-4">
                {question ? "Edit Question" : "Add Question"}
              </Dialog.Title>

              {formError && (
                <div className="mb-3 rounded-md bg-red-50 border border-red-200 p-2 text-sm text-red-700">
                  {formError}
                </div>
              )}

              <div className="space-y-4">
                <Field label="Question title">
                  <input
                    className={inputClass}
                    value={title}
                    onChange={({ target }) => {
                      setTitle(target.value);
                      if (!question && !key) {
                        setKey(slugifyKey(target.value).slice(0, 40));
                      }
                      setFormError("");
                    }}
                  />
                </Field>

                <div className="grid grid-cols-2 gap-4">
                  <Field
                    label="Key"
                    hint="Stable snake_case id — answers are stored under it. Don't change after publishing."
                  >
                    <input
                      className={`${inputClass} font-mono`}
                      value={key}
                      onChange={({ target }) => {
                        setKey(slugifyKey(target.value));
                        setFormError("");
                      }}
                    />
                  </Field>
                  <Field label="Type">
                    <select
                      className={selectClass}
                      value={type}
                      onChange={({ target }) => setType(target.value)}
                    >
                      {QUESTION_TYPES.map((entry) => (
                        <option key={entry.value} value={entry.value}>
                          {entry.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <Field label="Subtitle (optional)">
                  <input
                    className={inputClass}
                    value={subtitle}
                    onChange={({ target }) => setSubtitle(target.value)}
                  />
                </Field>

                {type !== "INFO" && (
                  <div className="flex items-center gap-3">
                    <Toggle enabled={required} onChange={() => setRequired(!required)} />
                    <span className="text-sm text-gray-700">Required</span>
                  </div>
                )}

                {isSelect && (
                  <Field label="Options" hint="Per-option disqualifier: WARN records it for the doctor; BLOCK ends the visit with the message.">
                    <div className="space-y-2 mt-1">
                      {options.map((option, index) => (
                        <div key={index} className="border border-gray-200 rounded-md p-2 space-y-2">
                          <div className="flex gap-2">
                            <input
                              className={`${inputClass} font-mono`}
                              placeholder="value (stored)"
                              value={option.value}
                              onChange={({ target }) => {
                                const next = [...options];
                                next[index] = { ...option, value: slugifyKey(target.value) };
                                setOptions(next);
                              }}
                            />
                            <input
                              className={inputClass}
                              placeholder="Label (shown)"
                              value={option.label}
                              onChange={({ target }) => {
                                const next = [...options];
                                next[index] = {
                                  ...option,
                                  label: target.value,
                                  value: option.value || slugifyKey(target.value),
                                };
                                setOptions(next);
                              }}
                            />
                            <select
                              className={selectClass}
                              value={option.disqualifyAction}
                              onChange={({ target }) => {
                                const next = [...options];
                                next[index] = { ...option, disqualifyAction: target.value };
                                setOptions(next);
                              }}
                            >
                              <option value="">No disqualifier</option>
                              <option value="WARN">WARN doctor</option>
                              <option value="BLOCK">BLOCK visit</option>
                            </select>
                            <Button
                              btnText="✕"
                              variant="Secondary"
                              className="h-9 px-2"
                              onClick={() =>
                                setOptions(options.filter((_, optionIndex) => optionIndex !== index))
                              }
                            />
                          </div>
                          {option.disqualifyAction && (
                            <input
                              className={inputClass}
                              placeholder={
                                option.disqualifyAction === "BLOCK"
                                  ? "Message shown to the patient when blocked"
                                  : "Note surfaced to the doctor"
                              }
                              value={option.disqualifyMessage}
                              onChange={({ target }) => {
                                const next = [...options];
                                next[index] = { ...option, disqualifyMessage: target.value };
                                setOptions(next);
                              }}
                            />
                          )}
                        </div>
                      ))}
                      <Button
                        btnText="Add option"
                        variant="Secondary"
                        className="h-8 px-2"
                        onClick={() =>
                          setOptions([...options, { value: "", label: "", disqualifyAction: "", disqualifyMessage: "" }])
                        }
                      />
                    </div>
                  </Field>
                )}

                {type === "TEXT" && (
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Min length">
                      <input type="number" className={inputClass} value={validation.minLength ?? ""} onChange={({ target }) => setValidationField("minLength", target.value)} />
                    </Field>
                    <Field label="Max length">
                      <input type="number" className={inputClass} value={validation.maxLength ?? ""} onChange={({ target }) => setValidationField("maxLength", target.value)} />
                    </Field>
                  </div>
                )}
                {type === "NUMBER" && (
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Min">
                      <input type="number" className={inputClass} value={validation.min ?? ""} onChange={({ target }) => setValidationField("min", target.value)} />
                    </Field>
                    <Field label="Max">
                      <input type="number" className={inputClass} value={validation.max ?? ""} onChange={({ target }) => setValidationField("max", target.value)} />
                    </Field>
                  </div>
                )}
                {type === "DATE" && (
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Earliest date" hint={'Tokens allowed: "today", "today-18y", "today+30d"'}>
                      <input className={inputClass} value={validation.minDate ?? ""} onChange={({ target }) => setValidation((current) => ({ ...current, minDate: target.value }))} />
                    </Field>
                    <Field label="Latest date">
                      <input className={inputClass} value={validation.maxDate ?? ""} onChange={({ target }) => setValidation((current) => ({ ...current, maxDate: target.value }))} />
                    </Field>
                  </div>
                )}
                {type === "FILE" && (
                  <Field label="Max photos">
                    <input type="number" className={inputClass} value={validation.maxFiles ?? ""} onChange={({ target }) => setValidationField("maxFiles", target.value)} />
                  </Field>
                )}
                {type === "MULTI_SELECT" && (
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Min selections">
                      <input type="number" className={inputClass} value={validation.minSelections ?? ""} onChange={({ target }) => setValidationField("minSelections", target.value)} />
                    </Field>
                    <Field label="Max selections">
                      <input type="number" className={inputClass} value={validation.maxSelections ?? ""} onChange={({ target }) => setValidationField("maxSelections", target.value)} />
                    </Field>
                  </div>
                )}
                {type === "WIDGET" && (
                  <div className="space-y-3">
                    <Field label="Widget">
                      <select className={selectClass} value={widget} onChange={({ target }) => setWidget(target.value)}>
                        {WIDGET_TYPES.map((entry) => (
                          <option key={entry.value} value={entry.value}>{entry.label}</option>
                        ))}
                      </select>
                    </Field>
                    <div className="grid grid-cols-2 gap-4 items-end">
                      <Field label="Included medications" hint="Each medication beyond this adds the Rx Surcharge fee">
                        <input type="number" className={inputClass} value={widgetConfig.includedMeds ?? ""} onChange={({ target }) => setWidgetConfig({ ...widgetConfig, includedMeds: target.value === "" ? undefined : Number(target.value) })} />
                      </Field>
                      <div className="flex items-center gap-2 pb-2">
                        <Toggle enabled={widgetConfig.allow90Day !== false} onChange={() => setWidgetConfig({ ...widgetConfig, allow90Day: widgetConfig.allow90Day === false })} />
                        <span className="text-sm text-gray-700">90-day supply</span>
                      </div>
                    </div>
                  </div>
                )}

                <Field
                  label="Show this question only if…"
                  hint="All rules must match (AND). Only earlier questions can be referenced."
                >
                  <div className="space-y-2 mt-1">
                    {showIf.map((condition, index) => (
                      <div key={index} className="flex gap-2">
                        <select
                          className={selectClass}
                          value={condition.questionKey}
                          onChange={({ target }) => {
                            const next = [...showIf];
                            next[index] = { ...condition, questionKey: target.value };
                            setShowIf(next);
                          }}
                        >
                          <option value="">Select question…</option>
                          {earlierQuestions.map((earlier) => (
                            <option key={earlier.key} value={earlier.key}>
                              {earlier.title || earlier.key}
                            </option>
                          ))}
                        </select>
                        <select
                          className={selectClass}
                          value={condition.op}
                          onChange={({ target }) => {
                            const next = [...showIf];
                            next[index] = { ...condition, op: target.value };
                            setShowIf(next);
                          }}
                        >
                          {CONDITION_OPS.map((op) => (
                            <option key={op.value} value={op.value}>{op.label}</option>
                          ))}
                        </select>
                        {condition.op !== "answered" && (
                          <input
                            className={inputClass}
                            placeholder='true / false / value / a,b'
                            value={condition.value}
                            onChange={({ target }) => {
                              const next = [...showIf];
                              next[index] = { ...condition, value: target.value };
                              setShowIf(next);
                            }}
                          />
                        )}
                        <Button
                          btnText="✕"
                          variant="Secondary"
                          className="h-9 px-2"
                          onClick={() => setShowIf(showIf.filter((_, conditionIndex) => conditionIndex !== index))}
                        />
                      </div>
                    ))}
                    <Button
                      btnText="Add rule"
                      variant="Secondary"
                      className="h-8 px-2"
                      isDisabled={earlierQuestions.length === 0}
                      onClick={() => setShowIf([...showIf, { questionKey: "", op: "eq", value: "true" }])}
                    />
                  </div>
                </Field>

                <Field
                  label="Disqualify on answer (question-level)"
                  hint="WARN records it for the doctor; BLOCK ends the visit and shows the message."
                >
                  {disqualify ? (
                    <div className="space-y-2 mt-1">
                      <div className="flex gap-2">
                        <select
                          className={selectClass}
                          value={disqualify.action}
                          onChange={({ target }) => setDisqualify({ ...disqualify, action: target.value })}
                        >
                          <option value="WARN">WARN doctor</option>
                          <option value="BLOCK">BLOCK visit</option>
                        </select>
                        <select
                          className={selectClass}
                          value={disqualify.op}
                          onChange={({ target }) => setDisqualify({ ...disqualify, op: target.value })}
                        >
                          {CONDITION_OPS.filter((op) => op.value !== "answered").map((op) => (
                            <option key={op.value} value={op.value}>{op.label}</option>
                          ))}
                        </select>
                        <input
                          className={inputClass}
                          placeholder='e.g. true'
                          value={disqualify.value}
                          onChange={({ target }) => setDisqualify({ ...disqualify, value: target.value })}
                        />
                        <Button
                          btnText="✕"
                          variant="Secondary"
                          className="h-9 px-2"
                          onClick={() => setDisqualify(null)}
                        />
                      </div>
                      <input
                        className={inputClass}
                        placeholder={disqualify.action === "BLOCK" ? "Message shown to the patient" : "Note surfaced to the doctor"}
                        value={disqualify.message}
                        onChange={({ target }) => setDisqualify({ ...disqualify, message: target.value })}
                      />
                    </div>
                  ) : (
                    <Button
                      btnText="Add disqualifier"
                      variant="Secondary"
                      className="h-8 px-2 mt-1"
                      onClick={() => setDisqualify(emptyDisqualifier())}
                    />
                  )}
                </Field>

              </div>

              <div className="mt-6 flex justify-end gap-2">
                <Button
                  btnText="Cancel"
                  variant="Secondary"
                  className="h-10 px-4"
                  onClick={() => setOpen(false)}
                />
                <Button btnText="Save Question" className="h-10 px-4" onClick={save} />
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
